// src/components/MaintenanceGate.jsx
// Pillar B3 — wraps the routed app. Blocks non-admin users with a
// MaintenancePage when System Settings → maintenance_mode is on.
// Uses the project's existing AuthContext (not @supabase/auth-helpers-react)
// to stay consistent with the rest of KaiLux.
//
// FIX (July 2026 — post-lockout incident):
// The gate previously wrapped EVERY route, including /login and /admin, and
// its only bypass required an already-live session with
// app_metadata.role === 'admin'. That meant a logged-out admin (session
// expired, new browser, cache cleared, etc.) had no way back in at all once
// maintenance mode was on — not even via /login. There was no route out of
// the maintenance page.
//
// Fix: always exempt the entry points a person needs to get back in or that
// must stay reachable regardless of app state — /login, /signup, /admin, the
// token-based portal links, and the offline shell — from the gate entirely.
// The admin-session bypass below is kept as a second, belt-and-braces check
// for any other route an admin might land on directly.
import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { getAnnouncement } from '../api/kailux'
import { MaintenancePage } from '../pages/MaintenancePage'

// Paths that must NEVER be blocked by maintenance mode, regardless of
// session state. This must include /login — without it, maintenance mode
// can lock everyone out permanently with no way back in. (/admin used to be
// listed here too, but that route no longer exists in this app at all — the
// Admin Board now lives on its own subdomain, admin.kailux.co.za, which runs
// a completely separate app (AdminApp.jsx) that never renders this gate.)
const ALWAYS_ALLOWED_PREFIXES = [
  '/login',
  '/signup',
  '/signoff/',
  '/intake/',
  '/offline',
  // Password reset (Aug 2026) — same reasoning as /login: a person locked
  // out of their account needs a way back in regardless of maintenance
  // state, and both legs of this flow run with no session at all.
  '/forgot-password',
  '/reset-password',
  // Admin -> client-app SSO landing page (30 Aug 2026) — reached with no
  // session yet (that's the entire point), so it can't depend on the
  // isAdmin bypass below, which needs a live session to evaluate.
  '/sso',
]

export function isAlwaysAllowedPath(pathname) {
  return ALWAYS_ALLOWED_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p)
  )
}

export function MaintenanceGate({ children }) {
  // BUG FIX (Aug 2026 — admin bypass never fired): this used to destructure
  // `session` from useAuth(), but AuthContext's provider value never
  // includes a `session` key — only `user`, `firm`, `loading`, `login`,
  // `logout`, `loadFirm` (grep confirms every other call site in the
  // frontend destructures `user`, never `session`). That meant `session`
  // was always `undefined` here, so `isAdmin` was always `false` for every
  // account, including genuine platform admins — the admin bypass this
  // whole component exists to provide had never actually worked. `user` is
  // already the Supabase auth User object (session.user), which carries
  // app_metadata the same way session.user did, so this is a drop-in fix.
  const { user } = useAuth()
  const [maintenanceMode, setMaintenanceMode] = useState(false)
  const [maintenanceMsg, setMaintenanceMsg] = useState('')

  const isAdmin = user?.app_metadata?.role === 'admin'
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  const isExemptPath = isAlwaysAllowedPath(path)

  useEffect(() => {
    // Don't even bother checking maintenance state for exempt paths — those
    // routes render unconditionally either way, and skipping the call avoids
    // a flash-of-maintenance-page while the request is in flight.
    if (isExemptPath) return

    getAnnouncement()
      .then(d => {
        setMaintenanceMode(!!d.maintenance_mode)
        setMaintenanceMsg(d.maintenance_message || '')
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExemptPath])

  if (maintenanceMode && !isAdmin && !isExemptPath) {
    return <MaintenancePage message={maintenanceMsg} />
  }

  return children
}
